import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { MENU_SORT_OPTIONS, normalizeMenuSortMode, sortMenuItems } from "../../lib/menuSorting";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, Trash2, Save } from "lucide-react";

const EMPTY = { section: "aperitivos", name: "", description: "", price: "", order: 0 };
const MENU_SECTIONS = [
  { value: "aperitivos", label: "Aperitivos" },
  { value: "comidas", label: "Platos principales" },
  { value: "montados", label: "Montados" },
  { value: "postres", label: "Postres" },
  { value: "bebidas", label: "Bebidas" },
];
const toPayload = (item) => ({
  section: item.section,
  name: item.name,
  description: item.description,
  price: item.price,
  order: item.order,
});

export default function AdminCarta() {
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState(EMPTY);
  const [menuSettings, setMenuSettings] = useState({});
  const [sortMode, setSortMode] = useState("manual");
  const [savingSortMode, setSavingSortMode] = useState(false);
  const [movingId, setMovingId] = useState(null);

  const load = useCallback(() => api.get("/menu").then((r) => setItems(r.data)), []);
  useEffect(() => {
    load();
    api.get("/content/menu_settings")
      .then((r) => {
        const settings = r.data.data || {};
        setMenuSettings(settings);
        setSortMode(normalizeMenuSortMode(settings.sort_mode));
      })
      .catch(() => {});
  }, [load]);

  const displayedItems = useMemo(() => {
    const groups = new Map();
    items.forEach((item) => {
      if (!groups.has(item.section)) groups.set(item.section, []);
      groups.get(item.section).push(item);
    });
    return Array.from(groups.values()).flatMap((group) => sortMenuItems(group, sortMode));
  }, [items, sortMode]);

  const create = async () => {
    if (!newItem.name) return toast.error("Añade un nombre");
    try {
      const categoryItems = items.filter((item) => item.section === newItem.section);
      const lastOrder = categoryItems.reduce((max, item) => {
        const order = Number(item.order);
        return Number.isFinite(order) ? Math.max(max, order) : max;
      }, 0);
      await api.post("/menu", { ...newItem, order: lastOrder + 10 });
      setNewItem(EMPTY);
      load();
      toast.success("Plato añadido");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const update = async (it) => {
    try {
      await api.put(`/menu/${it.id}`, toPayload(it));
      toast.success("Actualizado");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeSortMode = async (nextMode) => {
    const previousMode = sortMode;
    const nextSettings = { ...menuSettings, sort_mode: nextMode };
    setSortMode(nextMode);
    setSavingSortMode(true);
    try {
      await api.put("/content/menu_settings", { data: nextSettings });
      setMenuSettings(nextSettings);
      toast.success("Orden de la carta actualizado");
    } catch (e) {
      setSortMode(previousMode);
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingSortMode(false);
    }
  };

  const move = async (item, direction) => {
    const categoryItems = sortMenuItems(
      items.filter((candidate) => candidate.section === item.section),
      "manual",
    );
    const currentIndex = categoryItems.findIndex((candidate) => candidate.id === item.id);
    const targetIndex = currentIndex + direction;
    if (currentIndex < 0 || targetIndex < 0 || targetIndex >= categoryItems.length) return;

    const reordered = [...categoryItems];
    [reordered[currentIndex], reordered[targetIndex]] = [reordered[targetIndex], reordered[currentIndex]];
    const normalized = reordered.map((candidate, index) => ({ ...candidate, order: (index + 1) * 10 }));
    const updates = new Map(normalized.map((candidate) => [candidate.id, candidate]));

    setItems((current) => current.map((candidate) => updates.get(candidate.id) || candidate));
    setMovingId(item.id);
    try {
      await Promise.all(normalized.map((candidate) => api.put(`/menu/${candidate.id}`, toPayload(candidate))));
      await load();
      toast.success("Orden actualizado");
    } catch (e) {
      await load();
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setMovingId(null);
    }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar este plato?")) return;
    await api.delete(`/menu/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-carta">
      <header>
        <h1 className="font-display font-black text-3xl">Carta</h1>
        <p className="text-muted-foreground">Añade, edita y elimina platos y bebidas.</p>
      </header>

      <div className="flex items-center gap-3">
        <label htmlFor="menu-sort-mode" className="text-sm font-bold">Orden de la carta:</label>
        <select
          id="menu-sort-mode"
          value={sortMode}
          onChange={(e) => changeSortMode(e.target.value)}
          disabled={savingSortMode}
          className="rounded-lg border border-border px-3 py-2 text-sm disabled:opacity-60"
          data-testid="menu-sort-mode"
        >
          {MENU_SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-border p-5" data-testid="menu-create-form">
        <h2 className="font-display font-bold mb-3">Nuevo</h2>
        <div className="grid sm:grid-cols-6 gap-2">
          <select
            value={newItem.section}
            onChange={(e) => setNewItem({ ...newItem, section: e.target.value })}
            className="rounded-lg border border-border px-3 py-2"
            data-testid="new-section"
          >
            {MENU_SECTIONS.map((section) => (
              <option key={section.value} value={section.value}>{section.label}</option>
            ))}
          </select>
          <input placeholder="Nombre" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} className="sm:col-span-2 rounded-lg border border-border px-3 py-2" data-testid="new-name" />
          <input placeholder="Descripción" value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} className="sm:col-span-2 rounded-lg border border-border px-3 py-2" data-testid="new-description" />
          <input placeholder="Precio" value={newItem.price} onChange={(e) => setNewItem({ ...newItem, price: e.target.value })} className="rounded-lg border border-border px-3 py-2" data-testid="new-price" />
        </div>
        <button onClick={create} className="mt-3 inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-full font-bold" data-testid="menu-add-btn">
          <Plus size={16} /> Añadir plato
        </button>
      </div>

      <div className="space-y-2">
        {displayedItems.map((it) => {
          const categoryItems = sortMenuItems(
            items.filter((candidate) => candidate.section === it.section),
            "manual",
          );
          const categoryIndex = categoryItems.findIndex((candidate) => candidate.id === it.id);
          const moving = movingId != null;
          return (
            <div key={it.id} className="bg-white rounded-2xl border border-border p-4 grid sm:grid-cols-[120px_2fr_3fr_100px_auto] gap-2 items-center" data-testid={`menu-row-${it.id}`}>
              <select value={it.section} onChange={(e) => setItems((arr) => arr.map((x) => x.id === it.id ? { ...x, section: e.target.value } : x))} className="rounded-lg border border-border px-2 py-2 text-sm">
                {!MENU_SECTIONS.some((section) => section.value === it.section) && (
                  <option value={it.section} disabled hidden>Sin categoria visible</option>
                )}
                {MENU_SECTIONS.map((section) => (
                  <option key={section.value} value={section.value}>{section.label}</option>
                ))}
              </select>
              <input value={it.name} onChange={(e) => setItems((arr) => arr.map((x) => x.id === it.id ? { ...x, name: e.target.value } : x))} className="rounded-lg border border-border px-2 py-2 text-sm" />
              <input value={it.description} onChange={(e) => setItems((arr) => arr.map((x) => x.id === it.id ? { ...x, description: e.target.value } : x))} className="rounded-lg border border-border px-2 py-2 text-sm" />
              <input value={it.price} onChange={(e) => setItems((arr) => arr.map((x) => x.id === it.id ? { ...x, price: e.target.value } : x))} className="rounded-lg border border-border px-2 py-2 text-sm" />
              <div className="flex gap-1">
                {sortMode === "manual" && (
                  <>
                    <button
                      onClick={() => move(it, -1)}
                      disabled={moving || categoryIndex <= 0}
                      className="h-9 w-9 grid place-items-center rounded-lg border border-border disabled:opacity-30"
                      aria-label={`Subir ${it.name}`}
                      title="Subir"
                      data-testid={`menu-up-${it.id}`}
                    >
                      <ArrowUp size={14} />
                    </button>
                    <button
                      onClick={() => move(it, 1)}
                      disabled={moving || categoryIndex < 0 || categoryIndex >= categoryItems.length - 1}
                      className="h-9 w-9 grid place-items-center rounded-lg border border-border disabled:opacity-30"
                      aria-label={`Bajar ${it.name}`}
                      title="Bajar"
                      data-testid={`menu-down-${it.id}`}
                    >
                      <ArrowDown size={14} />
                    </button>
                  </>
                )}
                <button onClick={() => update(it)} className="h-9 w-9 grid place-items-center rounded-lg bg-primary text-primary-foreground" data-testid={`menu-save-${it.id}`}><Save size={14} /></button>
                <button onClick={() => remove(it.id)} className="h-9 w-9 grid place-items-center rounded-lg bg-destructive text-destructive-foreground" data-testid={`menu-delete-${it.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
